<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/table/PosTableOrdersList.vue.
  import type { IOrderListItem } from '@/domain/pos/pos-types';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';
  import PosV2TableOrderRow from './PosV2TableOrderRow.vue';

  const props = defineProps<{
    orders: IOrderListItem[];
    selectedIds: Set<number>;
    searching?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'toggle-select', order: IOrderListItem): void;
    (e: 'open-order', order: IOrderListItem): void;
  }>();

  function isSelected(order: IOrderListItem): boolean {
    return props.selectedIds.has(order.id);
  }
</script>

<template>
  <PosUiScrollable
    class="pos-v2-table-orders-list"
    orientation="vertical"
    variant="overlay"
    theme="page"
    :offset-top="10"
    :offset-bottom="10"
    size="md"
    scroll-class="orders-scroll"
  >
    <PosUiEmptyState
      v-if="orders.length === 0"
      :icon="searching ? 'magnify' : 'inbox-outline'"
      :title="searching ? $t('posV2.noResults') : $t('posV2.noOrdersOnTable')"
      class="empty-state"
    />

    <div v-else class="orders-grid">
      <PosV2TableOrderRow
        v-for="order in orders"
        :key="order.id"
        :order="order"
        :selected="isSelected(order)"
        @select="emit('toggle-select', order)"
        @open="emit('open-order', order)"
      />
    </div>
  </PosUiScrollable>
</template>

<style scoped lang="scss">
  .pos-v2-table-orders-list {
    @apply tw-relative tw-flex-1 tw-overflow-hidden;

    :deep(.orders-scroll) {
      @apply tw-h-full tw-overflow-y-auto tw-p-4;
    }

    .empty-state {
      @apply tw-h-full;
    }

    .orders-grid {
      @apply tw-grid tw-gap-3;
      // Viewport breakpoints lie here (admin sidebar + rail eat width):
      // let the container decide how many cards fit.
      grid-template-columns: repeat(auto-fill, minmax(min(290px, 100%), 1fr));
    }
  }
</style>
